import { IonIcon } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { chevronForwardOutline, timeOutline } from 'ionicons/icons'
import type { FragmentType } from '../../gql'
import { getFragmentData } from '../../gql'
import { AssignmentCardSlimFragment } from '../../graphql/assignments'
import {
  assignmentReportAt,
  hasReportAtWindow,
} from '../../helpers/assignmentReportAt'
import {
  type AssignmentStatusSlug,
  assignmentStatus,
  translateStatusText,
} from '../../helpers/assignmentStatus'
import { assignmentDate, formatRouteRelativeTime } from '../../helpers/datetime'
import { useAssignmentSiteTimes } from '../../hooks/useAssignmentSiteTimes'

type AssignmentCardSlimProps = {
  assignment: FragmentType<typeof AssignmentCardSlimFragment>
  /** Index for staggered entrance animation */
  index?: number
  reportAtDurationSeconds: number
}

const STATUS_CLASS_MAP: Record<AssignmentStatusSlug, string> = {
  attention: 'assignment-card--attention',
  confirmed: 'assignment-card--confirmed',
  inProgress: 'assignment-card--in-progress',
  completed: 'assignment-card--completed',
  cancelled: 'assignment-card--cancelled',
}

/**
 * Slim assignment card displayed in the assignment list.
 *
 * Layout: [Date Badge (full height)] [Arrow] [Content Column]
 *   Content Column = [Status + Details] + [Footer chips]
 */
export const AssignmentCardSlim = ({
  assignment: data,
  index = 0,
  reportAtDurationSeconds,
}: AssignmentCardSlimProps) => {
  const { _ } = useLingui()
  const assignment = getFragmentData(AssignmentCardSlimFragment, data)
  // Orgs that skip the yard see the site arrival instead of Report At
  const { onsiteAt } = useAssignmentSiteTimes(
    assignment.usesOnsiteTimes,
    assignment.route?.waypoints,
  )

  if (!assignment.route) return null

  const datetime = assignmentDate(assignment)
  if (!datetime) return null

  const status = assignmentStatus(assignment)
  const { equipment } = assignment.route
  const statusClass = STATUS_CLASS_MAP[status.slug] ?? ''
  // Shared with the detail hero so both screens show the same Report At
  const reportAt = assignmentReportAt(assignment.route, reportAtDurationSeconds)
  // With no report-ahead window the time is the departure itself (v1)
  const yardLabel = hasReportAtWindow(reportAtDurationSeconds)
    ? msg`Report At`
    : msg`Depart`
  // The label follows the value: the route start is never an onsite time
  const headlineAt = onsiteAt ?? reportAt
  const headlineLabel = onsiteAt ? msg`Onsite Time` : yardLabel

  return (
    <div
      className={`assignment-card ${statusClass}`}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      {/* Outer flex: badge spans full card height */}
      <div className='assignment-card__row'>
        {/* Filled date badge — full height */}
        <div className='assignment-card__date-badge'>
          <span className='assignment-card__day-of-week'>
            {datetime.toFormat('ccc').toUpperCase()}
          </span>
          <span className='assignment-card__date-text'>
            {datetime.toFormat('LLL dd').toUpperCase()}
          </span>
        </div>

        {/* Arrow divider */}
        <div className='assignment-card__divider' />

        {/* Right column: body + footer */}
        <div className='assignment-card__column'>
          <div className='assignment-card__body'>
            <div className='assignment-card__content'>
              <div className='assignment-card__status'>
                {translateStatusText(status.text, _)}
              </div>

              <div className='assignment-card__details'>
                {headlineAt && (
                  <div className='assignment-card__detail-item'>
                    <IonIcon
                      icon={timeOutline}
                      className='assignment-card__detail-icon'
                    />
                    <div>
                      <div className='assignment-card__detail-header'>
                        {_(headlineLabel)}
                      </div>
                      <div className='assignment-card__detail-body'>
                        {formatRouteRelativeTime(headlineAt, datetime)}
                      </div>
                    </div>
                  </div>
                )}

                {equipment && (
                  <div className='assignment-card__detail-item'>
                    <img
                      src='/assets/equipment.svg'
                      alt=''
                      aria-hidden='true'
                      className='assignment-card__detail-icon assignment-card__detail-icon--img'
                    />
                    <div>
                      <div className='assignment-card__detail-header'>
                        {_(msg`Asset`)}
                      </div>
                      <div className='assignment-card__detail-body'>
                        {equipment.name}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Chevron */}
            <div className='assignment-card__chevron'>
              <IonIcon icon={chevronForwardOutline} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
